<template>
  <validation-observer
    ref="form"
    v-slot="{ invalid }"
  >
    <am-modal
      :show="modalOpened"
      :title="farm && farm.id ? 'Edit Farm' : 'Add Farm'"
      size="lg"
      :loading="saving"
      :ok-disabled="invalid || saving"
      ok-text="Save"
      @onClose="$emit('modalClosed')"
      @onOk="save"
    >
      <div class="row">
        <div class="col-md-12 mb-1">
          <label>Farmer / Member</label>
          <div class="d-flex align-items-start farmer-search">
            <b-form-input
              v-model="farmerSearch"
              placeholder="Search farmer by name or ID..."
              class="farmer-search__input"
              @keyup.enter="searchFarmers"
            />
            <am-button
              class="farmer-search__btn ml-1"
              icon="SearchIcon"
              label="Search"
              variant="outline-primary"
              :loading="searching"
              @click="searchFarmers"
            />
          </div>
          <div class="mt-1">
            <am-select
              v-model="form.member_id"
              name="member_id"
              label="Select farmer"
              rules="required"
              placeholder="Select a farmer from search results"
              label-name="label"
              value-name="id"
              :options="farmerSelectOptions"
              :disabled="!farmerSelectOptions.length"
            />
          </div>
          <small
            v-if="searchDone && !farmerSelectOptions.length"
            class="text-muted"
          >
            No farmers found. Try another search.
          </small>
        </div>
        <div class="col-md-6">
          <am-input
            v-model="form.name"
            name="name"
            label="Farm name"
            rules="required"
          />
        </div>
        <div class="col-md-6">
          <am-input
            v-model="form.crop"
            name="crop"
            label="Primary crop"
            rules="required"
          />
        </div>
        <div class="col-md-6">
          <am-select
            v-model="form.land_ownership"
            name="land_ownership"
            label="Land ownership"
            rules="required"
            :options="landOwnershipOptions"
          />
        </div>
        <div class="col-md-6">
          <am-input
            v-model="form.size"
            name="size"
            label="Area (ha)"
            type="number"
            rules="required|numeric|min_value:0"
          />
        </div>
        <div class="col-md-6">
          <am-select
            v-model="form.farm_mapped"
            name="farm_mapped"
            label="Farm Mapped"
            rules="required"
            label-name="text"
            value-name="value"
            :options="yesNoOptions"
          />
        </div>
        <div
          v-if="form.farm_mapped"
          class="col-md-6"
        >
          <am-input
            v-model="form.mapped_by"
            name="mapped_by"
            label="Mapped By"
            rules="required"
          />
        </div>
        <div class="col-md-12">
          <am-input
            v-model="form.location"
            name="location"
            label="Farm location"
            rules="required"
          />
        </div>
        <div
          v-if="showGpsFields"
          class="col-md-12"
        >
          <am-input
            v-model="form.gps"
            name="gps"
            label="GPS centre (lat, lng)"
          />
        </div>
        <div
          v-if="showGpsFields"
          class="col-md-12"
        >
          <am-text-area
            v-model="form.polygon"
            name="polygon"
            label="Farm boundary polygon"
            rows="3"
            placeholder="lat,lng; lat,lng; lat,lng"
          />
        </div>
        <div class="col-12">
          <error-display :error="error" />
        </div>
      </div>
      <template slot="modal-actions">
        <b-row class="float-right">
          <am-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="$emit('modalClosed')"
          />
          <am-button
            name="save_farm"
            label="Save"
            :disabled="invalid || saving"
            :loading="saving"
            loading-text="Loading..."
            @click="save"
          />
        </b-row>
      </template>
    </am-modal>
  </validation-observer>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { BFormInput, BRow } from 'bootstrap-vue'
import AmModal from '@/farmbusiness/components/AmModal.vue'
import AmInput from '@/farmbusiness/components/AmInput.vue'
import AmSelect from '@/farmbusiness/components/AmSelect.vue'
import AmTextArea from '@/farmbusiness/components/AmTextArea.vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay.vue'
import showToast from '@/farmbusiness/mixins/showToast'

const emptyForm = () => ({
  member_id: null,
  name: null,
  crop: null,
  land_ownership: null,
  size: null,
  farm_mapped: false,
  mapped_by: null,
  location: null,
  gps: null,
  polygon: null,
})

export default {
  name: 'FarmSetup',
  components: {
    ValidationObserver,
    AmModal,
    AmInput,
    AmSelect,
    AmTextArea,
    AmButton,
    ErrorDisplay,
    BFormInput,
    BRow,
  },
  mixins: [showToast],
  props: {
    modalOpened: { type: Boolean, default: false },
    farm: { type: Object, default: null },
    presetMemberId: { type: [Number, String], default: null },
  },
  data() {
    return {
      form: emptyForm(),
      farmerSearch: '',
      farmerOptions: [],
      searching: false,
      searchDone: false,
      saving: false,
      error: {},
      // Temporarily hidden; set true to re-enable GPS/polygon fields
      showGpsFields: false,
      landOwnershipOptions: [
        'Owned', 'Rented', 'Leased', 'Customary/Family land', 'Sharecropping',
        'Communal land', 'Government allocation', 'Other',
      ],
      yesNoOptions: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
    }
  },
  computed: {
    farmerSelectOptions() {
      return this.farmerOptions
    },
  },
  watch: {
    modalOpened(opened) {
      if (opened) this.bootstrap()
    },
    'form.farm_mapped'(mapped) {
      if (!mapped) {
        this.form.mapped_by = null
      }
    },
  },
  methods: {
    bootstrap() {
      this.error = {}
      this.searchDone = false
      this.farmerSearch = ''
      this.farmerOptions = []
      this.$store.commit('farmManager/setFarmerMatches', [])

      if (this.farm && this.farm.id) {
        this.form = {
          member_id: this.farm.member_id,
          name: this.farm.name,
          crop: this.farm.crop,
          land_ownership: this.farm.land_ownership,
          size: this.farm.size,
          farm_mapped: this.farm.farm_mapped === true || this.farm.farm_mapped === 1 || this.farm.farm_mapped === '1',
          mapped_by: this.farm.mapped_by || null,
          location: this.farm.location,
          gps: this.farm.gps,
          polygon: this.farm.polygon,
        }
        if (this.farm.member) {
          this.farmerOptions = [{
            id: this.farm.member.id || this.farm.member_id,
            label: `${this.farm.member.full_name} (${this.farm.member.member_id})`,
          }]
        }
      } else {
        this.form = emptyForm()
        if (this.presetMemberId) {
          this.form.member_id = this.presetMemberId
          this.$store.dispatch('farmManager/searchFarmers', {
            search: '',
            limit: 5,
          }).then(matches => {
            const selected = (matches || []).find(m => String(m.id) === String(this.presetMemberId))
            if (selected) {
              this.farmerOptions = [selected]
            }
          })
        }
      }
    },
    searchFarmers() {
      const term = (this.farmerSearch || '').trim()
      if (!term) {
        this.showError('Enter a farmer name or ID to search')
        return
      }
      this.searching = true
      this.searchDone = false
      this.$store.dispatch('farmManager/searchFarmers', { search: term, limit: 5 })
        .then(matches => {
          this.farmerOptions = matches || []
          this.searchDone = true
          // Keep current selection if still in results; otherwise clear so user picks again
          if (this.form.member_id && !this.farmerOptions.some(m => String(m.id) === String(this.form.member_id))) {
            this.form.member_id = null
          }
          if (this.farmerOptions.length === 1) {
            this.form.member_id = this.farmerOptions[0].id
          }
        })
        .catch(() => {
          this.farmerOptions = []
          this.showError('Failed to search farmers')
        })
        .finally(() => {
          this.searching = false
        })
    },
    save() {
      this.$refs.form.validate().then(ok => {
        if (!ok) return
        if (!this.form.member_id) {
          this.showError('Please select a farmer')
          return
        }
        this.saving = true
        this.error = {}
        const payload = {
          ...this.form,
          farm_mapped: !!this.form.farm_mapped,
          mapped_by: this.form.farm_mapped ? this.form.mapped_by : null,
        }
        const action = this.farm && this.farm.id
          ? this.$store.dispatch('farmManager/updateFarm', { id: this.farm.id, data: payload })
          : this.$store.dispatch('farmManager/createFarm', payload)
        action.then(() => {
          this.showSuccess('Farm saved')
          this.$emit('saved')
        }).catch(err => {
          this.error = err.errors || { detail: err.message || 'Failed to save farm' }
          this.showError('Failed to save farm')
        }).finally(() => {
          this.saving = false
        })
      })
    },
  },
}
</script>

<style scoped>
.farmer-search {
  gap: 0.5rem;
}

.farmer-search__input {
  flex: 1 1 auto;
}

.farmer-search__btn {
  flex: 0 0 auto;
  white-space: nowrap;
}

.farmer-search__btn >>> .btn,
.farmer-search__btn >>> div {
  white-space: nowrap;
}
</style>
